import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { PageShell, Card } from "@/components/page-shell";
import { InlineLinks } from "@/components/checklist";
import { Faq, faqJsonLd } from "@/components/faq";
import { SourceList } from "@/components/source-list";
import { ShareCard } from "@/components/share-card";
import { IdentifyFlow } from "@/components/identify/identify-flow";
import { LifeStages } from "@/components/identify/life-stages";
import { useCalibration } from "@/hooks/use-calibration";
import { speciesForHub } from "@/lib/species";
import { breadcrumbJsonLd } from "@/lib/jsonld";
import { absUrl, ogImageUrl } from "@/lib/site";
import { getIdProfile, formatMm } from "@/lib/tick-id";
import { LIFE_STAGE_PHOTOS, PHIL_DETAILS, philCredit } from "@/lib/photos";
import { track } from "@/lib/analytics";
import { pageTitle } from "@/lib/seo";

const PATH = "/tick-identification";

const OG = ogImageUrl({
  title: "How to identify a Lone Star tick",
  subtitle: "Lone Star vs. deer vs. dog tick, with life stages",
  kind: "guide",
});

const faqItems = [
  {
    q: "What does a Lone Star tick look like?",
    a: "Adult female Lone Star ticks are reddish-brown with a single, distinctive white dot (the 'lone star') on the back. Males have scattered white streaks along the edge. Nymphs are very small, about the size of a poppy seed, which is why many bites go unnoticed.",
  },
  {
    q: "How is a Lone Star tick different from a deer tick?",
    a: "Lone Star ticks are larger and bite readily, and the females have one white dot. They are the tick most linked to alpha-gal syndrome. Deer ticks, also called blacklegged ticks, are smaller and darker, with an orange-red back half. They are the main tick that spreads Lyme disease.",
  },
  {
    q: "What does an American dog tick look like?",
    a: "American dog ticks are brown, with a gray or cream patterned shield behind the head. They tend to be larger and rounder, especially after a meal. They can spread Rocky Mountain spotted fever.",
  },
  {
    q: "How big are ticks at each life stage?",
    a: "For the Lone Star tick: larvae are about 0.5 mm (roughly a pinhead), nymphs about 1 to 1.5 mm (close to a poppy seed), and unfed adults about 3 to 4 mm (roughly a sesame seed). A fully fed female swells to about 10 to 16 mm, pea-sized. Nymphs cause many bites because they are so easy to miss.",
  },
  {
    q: "Can I identify a tick myself?",
    a: "The key on this page narrows the list by size, markings, leg colour and where you found it. It cannot give you a final answer, and some kinds of tick look the same to the eye. If it matters, seal the tick in a bag with a damp paper towel and take a clear photo. Then ask your state health department or a university extension program.",
  },
];

export const Route = createFileRoute("/tick-identification")({
  head: () => ({
    meta: [
      { title: pageTitle("Tick Identification: Lone Star vs. Deer vs. Dog Tick") },
      {
        name: "description",
        content:
          "Tell a Lone Star tick from a deer tick or dog tick by size, markings and leg colour, with photos of the larva, nymph and adult stages.",
      },
      { property: "og:title", content: "How to identify a Lone Star tick" },
      {
        property: "og:description",
        content:
          "Compare the Lone Star, deer, and dog tick with a clear visual guide and life-stage sizing.",
      },
      { property: "og:url", content: absUrl(PATH) },
      { property: "og:type", content: "article" },
      { property: "og:image", content: OG },
      { name: "twitter:title", content: "How to identify a Lone Star tick" },
      { name: "twitter:image", content: OG },
    ],
    links: [{ rel: "canonical", href: absUrl(PATH) }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(faqJsonLd(faqItems)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Tick identification", path: PATH },
          ]),
        ),
      },
      ...LIFE_STAGE_PHOTOS.map((photo) => ({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ImageObject",
          contentUrl: absUrl(photo.src),
          caption: `Lone Star tick (Amblyomma americanum), ${photo.label.toLowerCase()}, seen from above.`,
          creditText: `${photo.photographer}, CDC Public Health Image Library #${photo.philId}`,
          creator: {
            "@type": "Organization",
            name: "Centers for Disease Control and Prevention",
          },
          acquireLicensePage: PHIL_DETAILS(photo.philId),
          license: "https://en.wikipedia.org/wiki/Public_domain",
        }),
      })),
    ],
  }),
  component: TickIdentificationPage,
});

const READ_MORE = [
  { slug: "what-does-a-lone-star-tick-look-like", label: "What does it look like?" },
  { slug: "lone-star-tick-vs-deer-tick", label: "Lone Star vs. deer tick" },
  { slug: "where-do-lone-star-ticks-live", label: "Where they live" },
] as const;

function TickIdentificationPage() {
  const [pxPerMm, setPxPerMm] = useCalibration();

  useEffect(() => {
    track("tick_identifier_viewed", {});
  }, []);

  return (
    <PageShell
      crumbs={[
        { name: "Home", path: "/" },
        { name: "Tick identification", path: PATH },
      ]}
      title="How to identify a Lone Star tick"
      intro="Answer what you can see: its size, its markings, its legs and where you found it. The key below narrows our eight species down to the ones that fit. It helps you guess, but an expert has the final word."
    >
      <LifeStagePlate />

      <IdentifyFlow pxPerMm={pxPerMm} onCalibrate={setPxPerMm} />

      <LifeStages pxPerMm={pxPerMm} />

      <Card className="grid gap-x-12 gap-y-10 md:grid-cols-2">
        <div>
          <h2 className="font-display text-[1.75rem] font-medium leading-tight text-foreground">
            Full field guide by species
          </h2>
          <p className="mt-2 text-[0.9375rem] leading-relaxed text-muted-foreground">
            Each species page has its markings, size, range, and the diseases it carries.
          </p>
          <InlineLinks className="mt-4">
            {speciesForHub().map((s) => (
              <li key={s.slug}>
                <Link to="/ticks/$species" params={{ species: s.slug }} className="link">
                  {s.commonName}
                </Link>
              </li>
            ))}
          </InlineLinks>
          <p className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm">
            <Link to="/ticks" className="link font-semibold">
              All tick species
            </Link>
            <Link to="/diseases" className="link font-semibold">
              Tick-borne diseases
            </Link>
          </p>
        </div>

        <div>
          <h2 className="font-display text-[1.75rem] font-medium leading-tight text-foreground">
            Read more
          </h2>
          <ul className="mt-4 divide-y divide-border border-y border-border">
            {READ_MORE.map((g) => (
              <li key={g.slug}>
                <Link
                  to="/$guide"
                  params={{ guide: g.slug }}
                  className="link block py-3 text-[0.9375rem]"
                >
                  {g.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </Card>

      <Card>
        <h2 className="font-display text-[1.75rem] font-medium leading-tight text-foreground">
          Tick identification FAQ
        </h2>
        <div className="mt-5">
          <Faq items={faqItems} />
        </div>
      </Card>

      <SourceList
        ids={["cdc-tick-geography", "cdc-ags", "tickencounter", "uga-extension-ags"]}
        title="Sources"
      />

      <ShareCard
        title="How to identify a Lone Star tick"
        subtitle="Lone Star vs. deer vs. dog tick"
        kind="guide"
        summaryText={`Tick identification: Lone Star vs. deer vs. dog tick, with life stages.\nFree visual guide: https://lonestarmap.com${PATH}`}
        sharePath={PATH}
        shareText="A clear visual guide to identifying the Lone Star tick (linked to alpha-gal syndrome) vs. deer and dog ticks:"
      />
    </PageShell>
  );
}

/** The size each photographed stage reaches, from the Lone Star tick's own profile. */
function stageSize(stage: (typeof LIFE_STAGE_PHOTOS)[number]["stage"]): string {
  const profile = getIdProfile("lone-star-tick");
  if (!profile) return "";
  if (stage === "nymph") return formatMm(profile.stages.nymph);
  if (stage === "fed-female") return formatMm(profile.engorgedFemale);
  return formatMm(profile.stages.adult);
}

/**
 * Fig. 1: the Lone Star tick at four stages, photographed from above, set as a plate in a
 * printed guide. Four frames on one ground, a label and size under each, and the caption
 * and credit across the foot, so nothing floats in a corner. Not to scale; Fig. 2 below
 * draws them at their real sizes.
 */
function LifeStagePlate() {
  return (
    <figure>
      <ul className="grid grid-cols-2 gap-x-3 gap-y-5 sm:grid-cols-4 sm:gap-x-4">
        {LIFE_STAGE_PHOTOS.map((photo) => (
          <li key={photo.stage}>
            <img
              src={photo.src}
              alt={photo.alt}
              width={photo.width}
              height={photo.height}
              decoding="async"
              className="block aspect-square h-auto w-full border border-border bg-black object-cover"
            />
            <p className="mt-2 flex flex-wrap items-baseline justify-between gap-x-2 border-t border-foreground/50 pt-1.5">
              <span className="font-display text-lg leading-tight text-foreground">
                {photo.label}
              </span>
              <span className="tabular text-sm text-muted-foreground">
                {stageSize(photo.stage)}
              </span>
            </p>
          </li>
        ))}
      </ul>
      <figcaption className="mt-4 grid gap-x-10 gap-y-2 border-t border-border pt-3 text-sm leading-relaxed text-muted-foreground md:grid-cols-[minmax(0,1fr)_minmax(0,18rem)]">
        <p>
          <span className="font-semibold text-foreground">Fig. 1.</span> The Lone Star tick,{" "}
          <i>Amblyomma americanum</i>, from above. Only the adult female has the single cream spot.
          The male has pale streaks around the rear edge instead, and the nymph has no spot at all.
          After a meal a female swells and turns grey. Enlarged, not to scale: Fig. 2 shows their
          real sizes.
        </p>
        <p className="text-xs">
          {philCredit(LIFE_STAGE_PHOTOS)} PHIL pages:{" "}
          {LIFE_STAGE_PHOTOS.map((photo, i) => (
            <span key={photo.philId}>
              <a
                href={PHIL_DETAILS(photo.philId)}
                target="_blank"
                rel="noopener noreferrer"
                className="link"
              >
                {photo.label}
              </a>
              {i < LIFE_STAGE_PHOTOS.length - 1 ? " · " : ""}
            </span>
          ))}
        </p>
      </figcaption>
    </figure>
  );
}
